import { memo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MobileNavSheet } from "./MobileNavSheet";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import logo from "@/assets/klarfinans_logo_ny.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { 
  MortgagePage, 
  PrivateLoanPage, 
  BusinessLoanPage, 
  LoanWithoutUCPage, 
  KlarsprakPage, 
  ContactPage,
  MortgageCalculatorPage,
  PrivateLoanCalculatorPage,
  InterestRateCalculatorPage,
  KALPCalculatorPage
} from "@/routes/preloadableRoutes";

const Navigation = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Reset navigation visibility when route changes
  useEffect(() => {
    setIsVisible(true);
    setLastScrollY(0);
  }, [location.pathname]);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          if (currentScrollY < 50) {
            // Show menu when at the top of the page
            setIsVisible(true);
          } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
            // Hide menu when scrolling down (with more margin)
            setIsVisible(false);
          } else if (lastScrollY - currentScrollY > 5) {
            // Show menu when scrolling up (with hysteresis)
            setIsVisible(true);
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navLinks = [
    { label: "Bolån", path: `/${currentLanguage}/bolan` },
    { label: "Privatlån", path: `/${currentLanguage}/privatlan` },
    { label: "Lån utan UC", path: `/${currentLanguage}/lana-utan-uc` },
    { label: "Företagslån", path: `/${currentLanguage}/foretagslan` },
    { label: "Klarspråk", path: `/${currentLanguage}/klarsprak` },
  ];

  const toolsLinks = [
    { label: "Bolånekalkylator", path: `/${currentLanguage}/verktyg/bolanekalkylator`, preload: MortgageCalculatorPage.preload },
    { label: "Privatlånekalkylator", path: `/${currentLanguage}/verktyg/privatlanekalkylator`, preload: PrivateLoanCalculatorPage.preload },
    { label: "Räntekalkylator", path: `/${currentLanguage}/verktyg/rantekalkylator`, preload: InterestRateCalculatorPage.preload },
    { label: "KALP-kalkylator", path: `/${currentLanguage}/verktyg/kalp-kalkylator`, preload: KALPCalculatorPage.preload },
  ];

  // Route preload map
  const routePreloadMap: Record<string, { preload: () => void }> = {
    [`/${currentLanguage}/bolan`]: MortgagePage,
    [`/${currentLanguage}/privatlan`]: PrivateLoanPage,
    [`/${currentLanguage}/lana-utan-uc`]: LoanWithoutUCPage,
    [`/${currentLanguage}/foretagslan`]: BusinessLoanPage,
    [`/${currentLanguage}/klarsprak`]: KlarsprakPage,
    [`/${currentLanguage}/kontakt`]: ContactPage,
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border transition-all duration-300 ease-in-out ${
        isVisible ? 'translate-y-0 shadow-lg' : '-translate-y-full shadow-none'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to={`/${currentLanguage}`} className="flex items-center">
            <img 
              src={logo}
              srcSet={`${logo} 500w`}
              sizes="(max-width: 768px) 120px, 150px"
              alt="Klarfinans - Jämför lån och kreditkort" 
              className="h-10 md:h-12 w-auto transition-opacity hover:opacity-90 active:opacity-80"
              width="150"
              height="51"
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onMouseEnter={() => routePreloadMap[link.path]?.preload()}
              className="text-foreground transition-colors font-medium hover:text-primary active:text-primary/80"
            >
              {link.label}
            </Link>
          ))}
          
          <Link
            to={`/${currentLanguage}/kontakt`}
            onMouseEnter={() => ContactPage.preload()}
            className="text-foreground transition-colors font-medium hover:text-primary active:text-primary/80"
          >
            Kontakt
          </Link>

          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="text-foreground transition-colors font-medium hover:text-primary active:text-primary/80 inline-flex items-center gap-1">
                Verktyg
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[220px] mt-2">
              {toolsLinks.map((tool) => (
                <DropdownMenuItem
                  key={tool.path}
                  onClick={() => navigate(tool.path)}
                  onMouseEnter={() => tool.preload()}
                  className="cursor-pointer hover:bg-primary hover:text-white focus:bg-primary focus:text-white"
                >
                  {tool.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/${currentLanguage}/verktyg`)}
                className="cursor-pointer font-medium hover:bg-primary hover:text-white focus:bg-primary focus:text-white"
              >
                Se alla verktyg
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              variant="default" 
              size="lg"
              onClick={() => navigate(`/${currentLanguage}/privatlan`)}
              className="hover:bg-primary hover:shadow-none hover:translate-y-0"
            >
              Jämför lån nu
            </Button>
          </div>

          {/* Mobile Menu */}
          <MobileNavSheet />
        </div>

      </div>
    </nav>
  );
};

export default memo(Navigation);
